/**
 * DV-STAGE-003: Main Stage Tester Hook
 * Combines input computation, samples caching, session storage, and state management
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  StageId,
  StageTesterState,
  StageInputSource,
  SampleItem,
  LastOutputRef,
  StageOverrides,
  SessionStorageState
} from '@/types/stage-tester';
import { useEffectiveInput } from './useEffectiveInput';
import { useSessionStorage } from './useSessionStorage';
import { fetchSamples, ApiResponse, SamplesResponse } from '@/lib/api-client';
import { sessionOutputs } from '@/lib/session-outputs';

interface UseStageTesterProps {
  stage: StageId;
  onLog: (entry: { ts: string; level: 'debug' | 'info' | 'warn' | 'error'; text: string; meta?: any }) => void;
}

interface StageTesterHookResult {
  // Core state
  state: StageTesterState;

  // Input computation results
  effectiveInput: any;
  inputEnvelope: any;
  inputError: string | null;
  inputHash: string;

  // Samples
  samples: SampleItem[];
  isLoadingSamples: boolean;
  samplesError: string | null;

  // Actions
  setSource: (source: StageInputSource) => void;
  setSampleId: (sampleId: string) => void;
  setCustomJson: (json: string) => void;
  setLastOutputRef: (ref: LastOutputRef) => void;
  setOverrides: (overrides: StageOverrides) => void;

  // Utilities
  clearState: () => void;
  reloadSamples: () => Promise<void>;
  getStorageInfo: () => any;
}

export function useStageTester({ stage, onLog }: UseStageTesterProps): StageTesterHookResult {
  const [state, setState] = useState<StageTesterState>({
    source: 'sample', // Default to samples
    customJson: '',
    isLoadingSamples: false,
    isLoadingLastOutput: false,
    isValidatingCustom: false
  });

  // Use React Query for samples with production configuration
  const samplesQuery = useQuery<ApiResponse<SamplesResponse>, Error, SampleItem[]>({
    queryKey: ['demo', 'stage-tester', 'samples', stage],
    queryFn: () => fetchSamples(stage),
    select: (data: ApiResponse<SamplesResponse>) => data.data.items,
    staleTime: 5 * 60 * 1000, // 5 minutes - samples don't change often
    refetchInterval: false, // No automatic polling
    refetchOnWindowFocus: false, // Don't refetch on focus
    enabled: !!stage, // Only run if stage provided
  });

  const samples = samplesQuery.data || [];
  const isLoadingSamples = samplesQuery.isLoading;
  const samplesError = samplesQuery.error instanceof Error ? samplesQuery.error.message : null;

  // Debug logging for samples query state
  useEffect(() => {
    console.log(`[useStageTester] ${stage} query state:`, {
      status: samplesQuery.status,
      dataLength: samples.length,
      isLoading: samplesQuery.isLoading,
      error: samplesQuery.error,
      queryKey: ['demo', 'stage-tester', 'samples', stage],
      lastFetch: samplesQuery.dataUpdatedAt
    });
  }, [stage, samplesQuery.status, samples.length, samplesQuery.isLoading, samplesQuery.error, samplesQuery.dataUpdatedAt]);

  // Use ref to avoid infinite re-renders from onLog dependencies
  const onLogRef = useRef(onLog);
  onLogRef.current = onLog;

  // Session storage hooks (using stable onLog ref)
  const { loadState, saveState, clearState: clearSessionState, getStorageInfo } = useSessionStorage({
    stage,
    onLog: (entry) => onLogRef.current(entry)
  });

  // Load state on mount
  useEffect(() => {
    const restored = loadState();
    if (restored) {
      setState(prev => ({
        ...prev,
        source: restored.source,
        sampleId: restored.sampleId,
        customJson: restored.customJson || '',
        overrides: restored.overrides,
        lastOutputRef: restored.lastOutputRef
      }));

      onLogRef.current({
        ts: new Date().toISOString(),
        level: 'info',
        text: `Restored state for ${stage}`,
        meta: {
          stage,
          source: restored.source,
          hasSampleId: !!restored.sampleId,
          hasCustomJson: !!restored.customJson
        }
      });
    }
  }, [stage, loadState]);

  // Save state changes to session storage (debounced) - use JSON stringify for deep comparison
  const saveStateDebounced = useCallback(() => {
    const sessionState: SessionStorageState = {
      source: state.source,
      sampleId: state.sampleId,
      customJson: state.customJson,
      overrides: state.overrides,
      lastOutputRef: state.lastOutputRef
    };

    saveState(sessionState);
  }, [state.source, state.sampleId, state.customJson, JSON.stringify(state.overrides), JSON.stringify(state.lastOutputRef), saveState]);

  // Debounce state saving to avoid excessive writes
  useEffect(() => {
    const timeout = setTimeout(saveStateDebounced, 100); // 100ms debounce
    return () => clearTimeout(timeout);
  }, [saveStateDebounced]);

  // Log samples status changes from React Query
  useEffect(() => {
    if (samplesQuery.isSuccess && samples && samples.length > 0) {
      onLogRef.current({
        ts: new Date().toISOString(),
        level: 'info',
        text: `Samples loaded for ${stage}`,
        meta: { stage, count: samples.length }
      });
    } else if (samplesQuery.isError && samplesError) {
      onLogRef.current({
        ts: new Date().toISOString(),
        level: 'error',
        text: `Failed to load samples for ${stage}`,
        meta: { stage, error: samplesError }
      });
    }
  }, [stage, samples?.length, samplesError, samplesQuery.isSuccess, samplesQuery.isError]);

  // Update last output reference when source changes to last_output
  useEffect(() => {
    if (state.source === 'last_output') {
      const lastOutput = sessionOutputs.getLastOutputForStage(stage);

      if (lastOutput) {
        const ref: LastOutputRef = {
          origin: 'session',
          stage: lastOutput.stage,
          executionId: lastOutput.executionId,
          updatedAt: lastOutput.completedAt
        };

        setState(prev => ({
          ...prev,
          lastOutputRef: ref,
          lastOutputError: undefined
        }));

        onLogRef.current({
          ts: new Date().toISOString(),
          level: 'info',
          text: `Found last output for ${stage}`,
          meta: {
            stage,
            sourceStage: lastOutput.stage,
            executionId: lastOutput.executionId
          }
        });
      } else {
        setState(prev => ({
          ...prev,
          lastOutputRef: undefined,
          lastOutputError: 'No last output available'
        }));

        onLogRef.current({
          ts: new Date().toISOString(),
          level: 'warn',
          text: `No last output found for ${stage}`,
          meta: { stage }
        });
      }
    }
  }, [state.source, stage]);

  // Validate custom JSON
  const customJsonError = useMemo(() => {
    if (state.source !== 'custom' || !state.customJson?.trim()) {
      return null;
    }

    try {
      JSON.parse(state.customJson);
      return null;
    } catch (error) {
      return error instanceof Error ? error.message : 'Invalid JSON';
    }
  }, [state.source, state.customJson]);

  // Memoize state object to prevent useEffectiveInput recomputation
  const stableState = useMemo(() => ({
    ...state,
    customJsonError: customJsonError || undefined,
    samplesError: samplesError || undefined,
    lastOutputError: state.lastOutputError || undefined
  }), [
    state.source,
    state.sampleId,
    state.customJson,
    JSON.stringify(state.lastOutputRef),
    JSON.stringify(state.overrides),
    state.lastOutputError,
    customJsonError,
    samplesError
  ]);

  // Memoize samples to prevent unnecessary re-renders
  const stableSamples = useMemo(() => samples || [], [JSON.stringify(samples)]);

  // Compute effective input (using stable onLog ref)
  const effectiveInputResult = useEffectiveInput({
    stage,
    state: stableState,
    samples: stableSamples,
    onLog: (entry) => onLogRef.current(entry)
  });

  // Action handlers (removed logging to prevent feedback loops)
  const setSource = useCallback((source: StageInputSource) => {
    setState(prev => ({ ...prev, source }));
  }, []);

  const setSampleId = useCallback((sampleId: string) => {
    setState(prev => ({ ...prev, sampleId, source: 'sample' }));
  }, []);

  const setCustomJson = useCallback((customJson: string) => {
    setState(prev => ({ ...prev, customJson, source: 'custom' }));
  }, []);

  const setLastOutputRef = useCallback((lastOutputRef: LastOutputRef) => {
    setState(prev => ({ ...prev, lastOutputRef, source: 'last_output' }));
  }, []);

  const setOverrides = useCallback((overrides: StageOverrides) => {
    setState(prev => ({ ...prev, overrides }));
  }, []);

  const clearState = useCallback(() => {
    setState({
      source: 'sample',
      customJson: '',
      isLoadingSamples: false,
      isLoadingLastOutput: false,
      isValidatingCustom: false
    });

    clearSessionState();
  }, [clearSessionState]);

  const reloadSamples = useCallback(async () => {
    try {
      await samplesQuery.refetch();
      onLogRef.current({
        ts: new Date().toISOString(),
        level: 'info',
        text: `Samples reloaded for ${stage}`,
        meta: { stage }
      });
    } catch (error) {
      onLogRef.current({
        ts: new Date().toISOString(),
        level: 'error',
        text: `Failed to reload samples for ${stage}`,
        meta: { stage, error: error instanceof Error ? error.message : 'Unknown error' }
      });
    }
  }, [samplesQuery, stage]);

  return {
    // Core state
    state: {
      ...state,
      customJsonError: customJsonError || undefined,
      samplesError: samplesError || undefined,
      isLoadingSamples
    },

    // Input computation results
    effectiveInput: effectiveInputResult.input,
    inputEnvelope: effectiveInputResult.envelope,
    inputError: effectiveInputResult.error,
    inputHash: effectiveInputResult.hash,

    // Samples
    samples: samples || [],
    isLoadingSamples,
    samplesError,

    // Actions
    setSource,
    setSampleId,
    setCustomJson,
    setLastOutputRef,
    setOverrides,

    // Utilities
    clearState,
    reloadSamples,
    getStorageInfo
  };
}